import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { MasterSponsorship } from "@/types/sponsorship";

interface SponsoredPersonRowProps {
  sponsorship: MasterSponsorship;
  onRemove: (sponsorship: MasterSponsorship) => void;
  onResendPending?: (sponsorship: MasterSponsorship) => void;
  onCancelPending?: (sponsorship: MasterSponsorship) => void;
}

function formatSponsorshipStatus(sponsorship: MasterSponsorship): string {
  if (sponsorship.status === "active" && sponsorship.acceptedAt) {
    return `Accepted ${new Date(sponsorship.acceptedAt).toLocaleDateString()}`;
  }

  if (sponsorship.status === "pending" && sponsorship.pendingExpiresAt) {
    const resendState = sponsorship.invitationResent;
    const resendMessage =
      resendState.count > 0
        ? resendState.lastResentAt
          ? `, last resent ${new Date(resendState.lastResentAt).toLocaleDateString()}`
          : resendState.count === 1
            ? ", resent once"
            : `, resent ${resendState.count} times`
        : "";
    return `Pending until ${new Date(sponsorship.pendingExpiresAt).toLocaleDateString()}${resendMessage}`;
  }

  if (sponsorship.status === "expired" && sponsorship.pendingExpiresAt) {
    return `Expired ${new Date(sponsorship.pendingExpiresAt).toLocaleDateString()}`;
  }

  if (sponsorship.status === "removed" && sponsorship.removedAt) {
    return `Removed ${new Date(sponsorship.removedAt).toLocaleDateString()}`;
  }

  return sponsorship.status === "pending" ? "Invite pending" : sponsorship.status;
}

export const SponsoredPersonRow: React.FC<SponsoredPersonRowProps> = ({
  sponsorship,
  onRemove,
  onResendPending,
  onCancelPending,
}) => {
  const pendingBundleId = sponsorship.pendingBundleId;

  return (
    <div className="flex items-center justify-between gap-3 border-b py-3 last:border-b-0">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{sponsorship.invitedEmail}</p>
        <p className="text-xs text-muted-foreground">
          {formatSponsorshipStatus(sponsorship)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={sponsorship.status === "active" ? "default" : "secondary"}>
          {sponsorship.status}
        </Badge>
        {sponsorship.status === "active" ? (
          <Button variant="outline" size="sm" onClick={() => onRemove(sponsorship)}>
            Remove
          </Button>
        ) : null}
        {sponsorship.status === "pending" &&
        pendingBundleId &&
        onResendPending &&
        onCancelPending ? (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onResendPending(sponsorship)}
            >
              Resend
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCancelPending(sponsorship)}
            >
              Cancel
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
};
